import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { portalFor } from '../../lib/roles'
import type { Organization, UserProfile } from '../../types'
import { Alert, Button, Card, CardBody, CardHeader, EmptyState, Input, Skeleton } from '../../components/ui'
import { CloseIcon, UsersGroupIcon } from '../../components/icons'

export default function UnallocatedMiners() {
  const qc = useQueryClient()
  const { user } = useAuth()
  const isAdmin = portalFor(user) === 'admin'
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [showForm, setShowForm] = useState(false)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['gov', 'unallocated-miners'],
    queryFn: () => api.get<{ ok: boolean; miners: UserProfile[] }>('/gov/unallocated-miners'),
  })

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<{ ok: boolean; organization: Organization }>(
      '/gov/users/batch-cooperative', body,
    ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['gov', 'unallocated-miners'] })
      setSelected(new Set())
      setShowForm(false)
    },
  })

  function toggle(uid: string) {
    setSelected((s) => {
      const next = new Set(s)
      if (next.has(uid)) next.delete(uid); else next.add(uid)
      return next
    })
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    create.mutate({
      name: String(form.get('name') ?? '').trim(),
      region_id: String(form.get('region_id') ?? '').trim(),
      district_id: String(form.get('district_id') ?? '').trim(),
      uids: [...selected],
    })
  }

  const miners = data?.miners ?? []

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Unallocated miners</h1>
          <p className="page-head__sub">
            {isAdmin ? 'Registered miners with no cooperative yet, across the platform.' : 'Registered miners with no cooperative yet, in your jurisdiction.'}
          </p>
        </div>
        {selected.size >= 2 && (
          <>
            <Button className="fab-trigger__inline" icon={<UsersGroupIcon />} onClick={() => setShowForm((s) => !s)}>
              {showForm ? 'Cancel' : `Form a cooperative (${selected.size} selected)`}
            </Button>
            <button
              type="button" className="fab"
              onClick={() => setShowForm((s) => !s)}
              aria-label={showForm ? 'Cancel' : 'Form a cooperative'}
            >
              {showForm ? <CloseIcon size={22} /> : <UsersGroupIcon size={22} />}
            </button>
          </>
        )}
      </div>

      {showForm && (
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title="New cooperative" sub={`${selected.size} members selected below`} />
          <CardBody>
            <form onSubmit={onSubmit}>
              <div className="stack" style={{ gap: 'var(--sp-4)' }}>
                {create.isError && (
                  <Alert tone="danger">
                    {create.error instanceof ApiError ? create.error.message : 'Could not create that cooperative.'}
                  </Alert>
                )}
                <Input label="Cooperative name" name="name" required />
                <div className="grid grid--2">
                  <Input label="Province / region" name="region_id" />
                  <Input label="District" name="district_id" />
                </div>
                <div className="row">
                  <Button type="submit" loading={create.isPending}>Create cooperative</Button>
                </div>
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader title="Unallocated" sub={miners.length ? `${miners.length} miners` : undefined} />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(error as Error).message}</Alert></div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }}><Skeleton h={20} /></div>
          ) : miners.length === 0 ? (
            <EmptyState title="Everyone's allocated" body="No unallocated miners in your jurisdiction right now." />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th /><th>Name</th><th>Location</th></tr></thead>
                <tbody>
                  {miners.map((m) => (
                    <tr key={m.uid}>
                      <td style={{ width: 32 }}>
                        <input type="checkbox" checked={selected.has(m.uid)} onChange={() => toggle(m.uid)} />
                      </td>
                      <td><Link to={`/users/${m.uid}`}>{m.full_name || m.email}</Link></td>
                      <td>{[m.district_id, m.region_id].filter(Boolean).join(', ') || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}
